import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { RocFormData } from '../../../../types/RocFormData'
import { RocFormField } from '../../../../types/RocFormField'
import { RocAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Cognitive/Psychosocial Development subsection adapter
 * Handles communication, cognitive assessment, and substance abuse history (5 fields)
 */
export class CognitivePsychosocialAdapter extends BaseSubsectionAdapter<RocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Cognitive/Psychosocial assessment fields
    const communicationAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_COMMUNICATION_ASSESSMENT
    )

    const cognitiveAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_COGNITIVE_ASSESSMENT
    )

    const historySubstanceAbuse = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_HISTORY_SUBSTANCE_ABUSE
    )?.at(0)

    const substanceAbuseNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_SUBSTANCE_ABUSE_NOTES
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [RocFormField.COGNITIVE_PSYCHOSOCIAL_COMMUNICATION_ASSESSMENT]: communicationAssessment,
      [RocFormField.COGNITIVE_PSYCHOSOCIAL_COGNITIVE_ASSESSMENT]: cognitiveAssessment,
      [RocFormField.COGNITIVE_PSYCHOSOCIAL_HISTORY_SUBSTANCE_ABUSE]: historySubstanceAbuse,
      [RocFormField.COGNITIVE_PSYCHOSOCIAL_SUBSTANCE_ABUSE_NOTES]: substanceAbuseNotes,
      [RocFormField.COGNITIVE_PSYCHOSOCIAL_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RocFormField.COGNITIVE_PSYCHOSOCIAL_COMMUNICATION_ASSESSMENT)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_COMMUNICATION_ASSESSMENT,
        value: this.getFormDataField(RocFormField.COGNITIVE_PSYCHOSOCIAL_COMMUNICATION_ASSESSMENT)
      })
    }

    if (this.formDataFieldExists(RocFormField.COGNITIVE_PSYCHOSOCIAL_COGNITIVE_ASSESSMENT)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_COGNITIVE_ASSESSMENT,
        value: this.getFormDataField(RocFormField.COGNITIVE_PSYCHOSOCIAL_COGNITIVE_ASSESSMENT)
      })
    }

    if (this.formDataFieldExists(RocFormField.COGNITIVE_PSYCHOSOCIAL_HISTORY_SUBSTANCE_ABUSE)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_HISTORY_SUBSTANCE_ABUSE,
        value: this.getFormDataField(RocFormField.COGNITIVE_PSYCHOSOCIAL_HISTORY_SUBSTANCE_ABUSE)
      })
    }

    if (this.formDataFieldExists(RocFormField.COGNITIVE_PSYCHOSOCIAL_SUBSTANCE_ABUSE_NOTES)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_SUBSTANCE_ABUSE_NOTES,
        value: this.getFormDataField(RocFormField.COGNITIVE_PSYCHOSOCIAL_SUBSTANCE_ABUSE_NOTES)
      })
    }

    if (this.formDataFieldExists(RocFormField.COGNITIVE_PSYCHOSOCIAL_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_COGNITIVE_PSYCHOSOCIAL_DEV_SYSTEM_SUMMARY,
        value: this.getFormDataField(RocFormField.COGNITIVE_PSYCHOSOCIAL_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
